import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import HtmlViewer from '../HtmlViewer';
import { BookButtons } from './Accounting';
import type { AwAging, AwAgingBucket, AwAgingByCurrency, AwAgingRow, AwAgingApRow } from '../../../types';
import type { AwCtx } from '../ctx';
import { amt, baht, btn, btnPrimary, card, dmy, errBox, note, num, openBlob, pillAmber, pillGreen, pillRed, sectionHead, table, td, th } from '../ui';

// ── Reports panes (owner 2026-10-13) ────────────────────────────────────────────────────────────
// The statements are the server's documents: headline totals from /ledger-books/statements (the same
// JSON the statutory-books page showed), the statement itself inline as print HTML, the PDF a click
// away. Nothing is recomputed here. AR aging is the dashboard's receivables predicate bucketed by
// days past due on the server (/acct-workspace/aging) — merchant documents, decimal amounts, one
// card per currency and never a cross-currency total. The AP half of the same response is empty
// until a payables register exists; the pane says so in one line instead of showing a grey card.

function useStatements(ctx: AwCtx) {
  const [fs, setFs] = useState<any>(null);
  useEffect(() => {
    let alive = true;
    api.ledgerStatements(ctx.periodId).then((d: any) => { if (alive) setFs(d); }).catch(() => { if (alive) setFs(null); });
    return () => { alive = false; };
  }, [ctx.periodId, ctx.reload]);
  return fs;
}

export function PlPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const fs = useStatements(ctx);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {fs && (
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('pa_sb_pl_line', { income: baht(fs.pl?.totals?.income), expense: baht(fs.pl?.totals?.expense) })} = <b style={{ color: 'var(--text-primary)' }}>{baht(fs.pl?.totals?.profit)}</b>
          {(fs.pl?.warnings || []).map((w: string, i: number) => <span key={i} style={{ color: '#92400E', marginLeft: 12 }}><i className="ti ti-info-circle" aria-hidden /> {w}</span>)}
        </div>
      )}
      <BookButtons ctx={ctx} items={[{ key: 'pl', th: 'งบกำไรขาดทุน', glossKey: 'pa_fs_pl', path: 'statement/pl' }]} />
      <HtmlViewer base="ledger-books" path="statement/pl" qs={{ periodId: ctx.periodId }} reload={ctx.reload} />
    </div>
  );
}

export function BalanceSheetPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const fs = useStatements(ctx);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {fs && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5, color: 'var(--text-secondary)' }}>
          <span>{t('pa_sb_assets_line', { amount: baht(fs.sfp?.totals?.assets) })}</span>
          {fs.sfp && !fs.sfp.balanced && (
            <span style={{ color: '#B42318', fontWeight: 700 }}><i className="ti ti-alert-triangle" aria-hidden /> งบไม่ดุล — {t('pa_sb_unbalanced_why')}</span>
          )}
          {(fs.sfp?.warnings || []).filter((w: string) => !/ไม่ดุล/.test(w)).map((w: string, i: number) => (
            <span key={i} style={{ color: '#92400E' }}><i className="ti ti-info-circle" aria-hidden /> {w}</span>
          ))}
        </div>
      )}
      <BookButtons ctx={ctx} items={[{ key: 'sfp', th: 'งบแสดงฐานะการเงิน', glossKey: 'pa_fs_sfp', path: 'statement/sfp' }]} />
      <HtmlViewer base="ledger-books" path="statement/sfp" qs={{ periodId: ctx.periodId }} reload={ctx.reload} />
    </div>
  );
}

export function ReportsCashFlowPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const open = async (k: string, path: string) => {
    if (busy) return; setBusy(k); setErr('');
    try { openBlob(await api.axDocUrl(path, { clientId: ctx.clientId, periodId: ctx.periodId })); } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={card}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => open('cf', 'cash-flow.pdf')} disabled={!!busy} style={{ ...btnPrimary, opacity: busy === 'cf' ? 0.6 : 1 }}>
            <i className={busy === 'cf' ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> งบกระแสเงินสด · {t('aw_cf_open')}
          </button>
          <button onClick={() => open('notes', 'notes.pdf')} disabled={!!busy} style={{ ...btn, opacity: busy === 'notes' ? 0.6 : 1 }}>
            <i className={busy === 'notes' ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> หมายเหตุประกอบงบการเงิน · {t('aw_notes_open')}
          </button>
        </div>
        {err && <div style={{ ...errBox, marginTop: 10 }}>{err}</div>}
      </div>
      {/* The statement itself, inline: accounting-extras renders cash-flow.pdf as print HTML on ?format=html
          exactly like the ledger books do, so the same viewer shows it without a second renderer. */}
      <HtmlViewer base="accounting-extras" path="cash-flow.pdf" qs={{ clientId: ctx.clientId, periodId: ctx.periodId }} reload={ctx.reload} />
    </div>
  );
}

// Not yet due reads calm; the first two overdue buckets amber; beyond 60 days red.
const bucketPill = (b: string): React.CSSProperties => b === 'current' ? pillGreen : (b === 'd1_30' || b === 'd31_60') ? pillAmber : pillRed;

/** One line of either side of the aging response, normalised for the table: AR rows name a customer and
 *  an invoice, AP rows a vendor and a bill, and the columns differ only in their headings. `agedFrom` is
 *  the date the server counted from — for AR its aged_from (due, else issue), for AP the due date the
 *  server already filled in (bill_date standing in). */
type AgingLine = { id: string; no: string; name: string; issued: string; due: string; agedFrom: string; days: number; bucket: string; currency: string; outstanding: number };
const arLine = (r: AwAgingRow): AgingLine => ({ id: r.id, no: r.doc_no, name: r.client_name, issued: r.issue_date, due: r.due_date, agedFrom: r.aged_from, days: r.days, bucket: r.bucket, currency: r.currency, outstanding: r.outstanding });
const apLine = (r: AwAgingApRow): AgingLine => ({ id: r.id, no: r.bill_no, name: r.vendor_name, issued: r.bill_date, due: r.due_date, agedFrom: r.due_date, days: r.days, bucket: r.bucket, currency: r.currency, outstanding: r.outstanding });

/** One side of the aging response: a card per currency with the five buckets, then the open documents.
 *  Shared by both sides — the AP half (batch 3) differs only in what the row's columns are called. */
export function AgingSection({ buckets, byCurrency, rows, emptyKey, nameKey, issuedKey, showRows = true }: {
  buckets: AwAgingBucket[]; byCurrency: AwAgingByCurrency[]; rows: AgingLine[]; emptyKey: string; nameKey: string; issuedKey: string; showRows?: boolean;
}) {
  const { t } = useTranslation();
  if (!byCurrency.length) return <div style={{ ...note, fontSize: 12.5 }}>{t(emptyKey)}</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* ⛔ One card per currency, no grand total: the owner console never adds currencies together. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {byCurrency.map(c => (
          <div key={c.currency} style={{ ...card, flex: '1 1 340px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{c.currency}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('aw_aging_total')}: <b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{amt(c.total)}</b></span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('aw_aging_count')}: <b style={{ color: 'var(--text-primary)' }}>{c.count}</b></span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))`, gap: 6 }}>
              {buckets.map(b => {
                const v = Number(c[b]) || 0;
                return (
                  <div key={b} style={{ padding: '6px 8px', borderRadius: 8, background: 'var(--bg-app)', border: '1px solid var(--border)', minWidth: 0 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(`aw_aging_${b}`)}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', marginTop: 2, color: !v ? 'var(--text-tertiary)' : b === 'current' ? 'var(--text-primary)' : '#B42318' }}>{amt(v)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {showRows && (
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_col_no')}</th>
                <th style={th}>{t(nameKey)}</th>
                <th style={th}>{t(issuedKey)}</th>
                <th style={th}>{t('aw_col_due')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_aging_days')}</th>
                <th style={th}>{t('aw_aging_bucket')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_col_outstanding')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.no}</td>
                  <td style={td}>{r.name}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(r.issued)}</td>
                  {/* A document with no terms carries no due date: an em dash and "from issue date" in muted text, never
                      the issue date dressed up as a due date (agedFrom is the date the server counted from). */}
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.due ? dmy(r.due) : (
                    <span style={{ color: 'var(--text-tertiary)' }} title={t('aw_aging_no_due')}>— <span style={{ fontSize: 11 }}>{t('aw_aging_from_issue')}</span></span>
                  )}</td>
                  <td style={num}>{Number(r.days) > 0 ? r.days : '—'}</td>
                  <td style={td}><span style={bucketPill(r.bucket)}>{t(`aw_aging_${r.bucket}`)}</span></td>
                  <td style={num}>{amt(r.outstanding)} <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>{r.currency}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ArApAgingPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [data, setData] = useState<AwAging | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setErr('');
    api.awAging(ctx.clientId).then((d: AwAging) => { if (alive) setData(d); }).catch((e: any) => { if (alive) { setData(null); setErr(String(e?.message || 'error')); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {err && <div style={errBox}>{err}</div>}
      {!data && !err && <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>}
      {data && (
        <>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            <i className="ti ti-calendar" aria-hidden /> {t('aw_aging_as_of', { date: dmy(data.asOf) })}
            <span style={{ ...note, marginLeft: 12 }}>{t('aw_ar_never_summed')}</span>
          </div>
          <div>
            <div style={sectionHead}>{t('aw_ar_by_currency')}</div>
            <AgingSection buckets={data.buckets} byCurrency={data.ar.byCurrency} rows={data.ar.rows.map(arLine)} emptyKey="aw_ar_none" nameKey="aw_col_customer" issuedKey="aw_aging_issue" />
            {/* Why the dashboard's receivables card and this pane can disagree on the same no-terms invoice: /ar reads
                "no due date" as not overdue, aging counts from the issue date. Said on screen only when it applies. */}
            {data.ar.rows.some(r => !r.due_date) && <div style={{ ...note, marginTop: 6 }}>{t('aw_aging_due_note')}</div>}
          </div>
          <div>
            <div style={sectionHead}>{t('aw_ap_title')}</div>
            {/* The payables register (batch 3): open bills aged from their due date, the bill date standing in when
                none was set — the server fills that in, so the Due column is never empty here. One plain line when
                the register is empty or the server does not answer the AP side. */}
            {data.ap.available
              ? <AgingSection buckets={data.buckets} byCurrency={data.ap.byCurrency} rows={data.ap.rows.map(apLine)} emptyKey="aw_aging_ap_none" nameKey="aw_pay_vendor" issuedKey="aw_pay_bill_date" />
              : <div style={{ ...note, fontSize: 12.5 }}>{t('aw_aging_ap_none')}</div>}
          </div>
        </>
      )}
      {ctx.periodId && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>{t('aw_sub_ledgers')}</div>
          <BookButtons ctx={ctx} items={[
            { key: 'ar', th: 'บัญชีย่อยลูกหนี้', glossKey: 'pa_sb_c_ar', path: 'subsidiary/ar' },
            { key: 'ap', th: 'บัญชีย่อยเจ้าหนี้', glossKey: 'pa_sb_c_ap', path: 'subsidiary/ap' },
          ]} />
        </div>
      )}
    </div>
  );
}
